import { DownloadIcon } from '@/assets/download-icon';
import { MyProfileAvatar } from '@/components/my-profile-avatar';
import { QuickActionsNav } from '@/components/quick-actions-nav';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { CopyText } from '@/components/ui/copy-text';
import { useGetPlantQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant.ts';
import { useGetPlantConnectionCertificateQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-connection-certificate.ts';
import { useMediaQuery } from '@/hooks/use-media-query';
import { ChevronLeft, TriangleAlert } from 'lucide-react';
import { Link, useParams } from 'react-router';
import PlantUptimeStatusPopover from '@/features/plants/components/plant-monitoring/plant-uptime-status-popover';
import { PlantMenuPopover } from '@/features/plants/components/plant-overview/plant-menu-popover';
import { Skeleton } from '@/components/ui/skeleton';

const PlantConnectionDetails = () => {
  const isMobile = useMediaQuery('(max-width:640px)');

  const { plantId } = useParams();
  const { data: plant, isLoading: isPlantLoading } = useGetPlantQuery({
    plantId: plantId || '',
  });

  const getPlantConnectionCertificateMutation = useGetPlantConnectionCertificateQuery({
    onSuccess: (data) => {
      if (data) {
        const fileName =
          data.type === 'certificate-pem'
            ? 'device-certificate.pem.crt'
            : data.type === 'private-key'
              ? 'private.pem.key'
              : data.type === 'certificate-ca'
                ? 'certificate-ca.pem'
                : '';

        if (!fileName || !data.text) return;

        const file = new File([data.text], fileName, {
          type: 'text/plain',
        });

        const fileURL = URL.createObjectURL(file);

        const downloadLink = document.createElement('a');
        downloadLink.href = fileURL;
        downloadLink.download = file.name;
        document.body.appendChild(downloadLink);
        downloadLink.click();

        document.body.removeChild(downloadLink);
      }
    },
  });

  return (
    <div className="w-full gap-2 overflow-auto px-4 pt-3 pb-8 sm:px-6">
      <div className="flex flex-col gap-6">
        <div className="flex h-[45px] items-center justify-between">
          {!isMobile && (
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild className="link-underline">
                    <Link to="/dashboard">Dashboard</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild className="link-underline">
                    <Link to="/plants">Plants</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild className="link-underline">
                    <Link to={`/plants/${plantId}`}>{plant?.basicDetails?.name}</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="font-semibold">Connection details</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          )}
          {isMobile && (
            <Button variant="outline" asChild className="size-8 p-0">
              <Link to="/plants">
                <ChevronLeft />
              </Link>
            </Button>
          )}

          <div className="flex gap-2">
            <QuickActionsNav />
            <MyProfileAvatar />
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {isPlantLoading ? (
            <Skeleton className="h-8 w-32" />
          ) : (
            <div className="flex w-full justify-between">
              <h2 className="text-2xl font-semibold">{plant?.basicDetails.name}</h2>
              <div className="flex items-center gap-2">
                <PlantUptimeStatusPopover />
                <PlantMenuPopover isLoading={isPlantLoading} />
              </div>
            </div>
          )}

          <div className="max-w-3xl">
            <div className="flex flex-col gap-2 rounded-xl bg-white p-3">
              <h3 className="font-medium">Endpoint URL and Port</h3>
              <CopyText
                label={'a3mkkjy3yck49f-ats.iot.eu-central-1.amazonaws.com'}
                content={'a3mkkjy3yck49f-ats.iot.eu-central-1.amazonaws.com'}
              />
              <CopyText label="8883" content="8883" />
            </div>

            <div className="flex flex-col gap-2 rounded-xl bg-white p-3">
              <h3 className="font-medium">Company Id</h3>
              <CopyText label={plant?.companyId || ''} content={plant?.companyId || ''} />

              <h3 className="font-medium">Plant Id</h3>
              <CopyText label={plantId || ''} content={plantId || ''} />
            </div>

            <div className="flex flex-col gap-4 rounded-xl bg-white p-3">
              <div className="flex flex-col gap-2">
                <h3 className="font-medium">Download certificates and keys</h3>
                <p className="text-sm">
                  Download and install the certificate and key files to your device so that it
                  can connect securely to MQTT broker.
                </p>
              </div>

              <Alert variant="warning">
                <TriangleAlert className="h-4 w-4" />
                <AlertTitle>Be careful!</AlertTitle>
                <AlertDescription>Don't share with anyone these files</AlertDescription>
              </Alert>

              <div className="flex flex-col gap-6">
                <div className="flex justify-between gap-2">
                  <div className="flex flex-col">
                    <span className="font-medium">Device certificate</span>
                    <span className="text-sm">device-certificate.pem.crt</span>
                  </div>
                  <Button
                    onClick={() => {
                      if (plantId) {
                        getPlantConnectionCertificateMutation.mutate({
                          plantId,
                          certificateType: 'certificate-pem',
                        });
                      }
                    }}
                    variant="outline"
                    className="flex gap-2"
                  >
                    <DownloadIcon height="18" />
                    <span>Download</span>
                  </Button>
                </div>
                <div className="flex justify-between gap-2">
                  <div className="flex flex-col">
                    <span className="font-medium">Private key file</span>
                    <span className="text-sm">private.pem.key</span>
                  </div>
                  <Button
                    onClick={() => {
                      if (plantId) {
                        getPlantConnectionCertificateMutation.mutate({
                          plantId,
                          certificateType: 'private-key',
                        });
                      }
                    }}
                    variant="outline"
                    className="flex gap-2"
                  >
                    <DownloadIcon height="18" />
                    <span>Download</span>
                  </Button>
                </div>
                <div className="flex justify-between gap-2">
                  <div className="flex flex-col">
                    <span className="font-medium">Root CA certificate</span>
                    <span className="text-sm">certificate-ca.pem</span>
                  </div>
                  <Button
                    onClick={() => {
                      if (plantId) {
                        getPlantConnectionCertificateMutation.mutate({
                          plantId,
                          certificateType: 'certificate-ca',
                        });
                      }
                    }}
                    variant="outline"
                    className="flex gap-2"
                  >
                    <DownloadIcon height="18" />
                    <span>Download</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlantConnectionDetails;
